import { z } from 'zod';
import { AddressSchema, TimestampsSchema } from './common.js';
// =============================================================================
// Property Schema - Real estate listing with tenant isolation
// =============================================================================
// SCHEMA v3.2 - Shadow Property Support (Auto-Add Shared Properties)
// - Added isShadow boolean for shadow properties
// - Added upstream object for linking to source property
// - Shadow properties enable buyer agents to have local workspace for shared props
//
// SCHEMA v3.1 - Agent representation and team permissions
// - Added representation: 'SELLER' | 'BUYER' (replaces isListing boolean)
// - Added team[] array for internal users with roles and permissions
// - isListing and assignedAgentIds deprecated (kept for backward compat)
// =============================================================================
/**
 * Property status
 */
export const PropertyStatusEnum = z.enum(['ACTIVE', 'PENDING', 'SOLD', 'ARCHIVED']);
// =============================================================================
// v3.1: Agent Representation and Team Types
// =============================================================================
/**
 * Agent representation - which side the agent represents
 */
export const RepresentationEnum = z.enum(['SELLER', 'BUYER']);
/**
 * Team member roles for internal users
 */
export const TeamRoleEnum = z.enum([
    'OWNER', // Created the property, full control
    'LISTING_AGENT', // Listing side agent
    'CO_LISTING_AGENT', // Co-listing agent (co-owner)
    'BUYER_AGENT', // Buyer side agent
    'TRANSACTION_COORDINATOR', // TC with upload/docusign access
    'ASSISTANT', // View-only assistant
]);
/**
 * Team member permissions
 */
export const TeamPermissionEnum = z.enum([
    'VIEW', // Can view property
    'EDIT', // Can edit property details
    'UPLOAD', // Can upload documents
    'DOCUSIGN', // Can send to DocuSign
    'SHARE', // Can share property
    'MANAGE_TEAM', // Can add/remove team members
    'DELETE', // Can archive/delete property
]);
/**
 * Team member schema - internal user's relationship to a property
 */
export const TeamMemberSchema = z.object({
    userId: z.string(),
    role: TeamRoleEnum,
    permissions: z.array(TeamPermissionEnum).default(['VIEW']),
    addedAt: z.coerce.date(),
    addedBy: z.string().nullable(),
});
/**
 * Default permissions by team role
 */
export const DEFAULT_TEAM_PERMISSIONS = {
    OWNER: ['VIEW', 'EDIT', 'UPLOAD', 'DOCUSIGN', 'SHARE', 'MANAGE_TEAM', 'DELETE'],
    LISTING_AGENT: ['VIEW', 'EDIT', 'UPLOAD', 'DOCUSIGN', 'SHARE'],
    CO_LISTING_AGENT: ['VIEW', 'EDIT', 'UPLOAD', 'DOCUSIGN', 'SHARE'],
    BUYER_AGENT: ['VIEW', 'UPLOAD', 'SHARE'],
    TRANSACTION_COORDINATOR: ['VIEW', 'UPLOAD', 'DOCUSIGN'],
    ASSISTANT: ['VIEW'],
};
/**
 * Party roles - who can be associated with a property
 */
export const PartyRoleEnum = z.enum([
    'PRIMARY_BUYER',
    'CO_BUYER',
    'PRIMARY_SELLER',
    'CO_SELLER',
    'LISTING_AGENT',
    'CO_LISTING_AGENT',
    'BUYER_AGENT',
    'TRANSACTION_COORDINATOR',
    'INSPECTOR',
    'LENDER',
    'ESCROW',
    'OTHER',
]);
/**
 * Party permissions
 */
export const PartyPermissionEnum = z.enum(['VIEW', 'UPLOAD', 'DOCUSIGN', 'OFFERS']);
/**
 * Party schema - represents a contact's relationship to a property
 */
export const PartySchema = z.object({
    contactId: z.string(),
    role: PartyRoleEnum,
    permissions: z.array(PartyPermissionEnum).default(['VIEW']),
    addedAt: z.coerce.date(),
    addedBy: z.string().nullable(),
});
/**
 * Property stats - denormalized counts
 */
export const PropertyStatsSchema = z.object({
    documentCount: z.number().default(0),
    viewerCount: z.number().default(0),
    offerCount: z.number().default(0),
    pendingOfferCount: z.number().default(0),
    unseenOfferCount: z.number().default(0),
    noteCount: z.number().default(0),
});
/**
 * DocuSign envelope tracking
 */
export const EnvelopeRecordSchema = z.object({
    envelopeId: z.string(),
    createdAt: z.coerce.date(),
    createdBy: z.string(), // User ID
    status: z.string().optional(),
});
// =============================================================================
// v3.3: Location Intelligence (fetched at property creation, not PDF upload)
// =============================================================================
/**
 * Location intelligence status
 */
export const LocationIntelligenceStatusEnum = z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED']);
/**
 * Location scores (1-10 scale for schools/safety/neighborhood/investment)
 */
export const PropertyLocationScoresSchema = z.object({
    schools: z.number().int().min(0).max(10).optional(),
    safety: z.number().int().min(0).max(10).optional(),
    neighborhood: z.number().int().min(0).max(10).optional(),
    investment: z.number().int().min(0).max(10).optional(),
});
/**
 * Structured neighborhood information (v3.6.1: added safetyInfo + investmentInfo)
 */
export const PropertyNeighborhoodInfoSchema = z.object({
    locationDemographics: z.string().optional(),
    schoolsEducation: z.string().optional(),
    amenitiesTransportation: z.string().optional(),
    safetyInfo: z.string().optional(),
    investmentInfo: z.string().optional(),
});
/**
 * Location intelligence data stored on property
 * Fetched at property creation time, decoupled from document processing
 */
export const LocationIntelligenceSchema = z.object({
    status: LocationIntelligenceStatusEnum,
    fetchedAt: z.coerce.date().optional(),
    errorMessage: z.string().optional(),
    locationScores: PropertyLocationScoresSchema.optional(),
    neighborhoodSummary: z.string().optional(),
    neighborhoodInfo: PropertyNeighborhoodInfoSchema.optional(),
    // Singleton-fetch claim bookkeeping (same pattern as MarketDataSchema).
    requestedAt: z.coerce.date().optional(),
    activeJobId: z.string().optional(),
});
// =============================================================================
// v5.0: Aggregated Insights (from property-level synthesis)
// =============================================================================
/**
 * Aggregated insights status
 */
export const AggregatedInsightsStatusEnum = z.enum(['PENDING', 'PROCESSING', 'SYNTHESIZING', 'COMPLETED', 'FAILED']);
/**
 * Aggregated insights summary stored on property
 * Full insights are in S3 at analysisS3Key
 */
export const AggregatedInsightsSchema = z.object({
    status: AggregatedInsightsStatusEnum,
    houseScore: z.number().int().min(0).max(100).optional(),
    documentCount: z.number().int().min(0).optional(),
    criticalIssueCount: z.number().int().min(0).optional(),
    positiveCount: z.number().int().min(0).optional(),
    synthesizedAt: z.coerce.date().optional(),
    errorMessage: z.string().optional(),
    // S3 keys for full data
    analysisS3Key: z.string().optional(), // property-analysis.json (full insights)
    contextS3Key: z.string().optional(), // property-context.json (for chat)
});
// =============================================================================
// v8.0: Market Data (Perplexity Agent API)
// =============================================================================
export const MarketDataStatusEnum = z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED']);
export const MarketDataSchema = z.object({
    status: MarketDataStatusEnum,
    fetchedAt: z.coerce.date().optional(),
    errorMessage: z.string().optional(),
    analysisS3Key: z.string().optional(), // market-data-{jobId}.json (live pointer)
    estimatedValue: z.number().optional(), // Quick-access field
    compsCount: z.number().int().min(0).optional(),
    rentalEstimate: z.number().optional(),
    // Singleton-fetch claim bookkeeping: only one FETCH_MARKET_DATA job may be
    // in flight per property. requestedAt enables stale-claim recovery (15 min);
    // activeJobId lets the completion handler reject zombie/duplicate results.
    requestedAt: z.coerce.date().optional(),
    activeJobId: z.string().optional(),
});
// =============================================================================
// v3.2: Shadow Property Support
// =============================================================================
/**
 * Upstream schema - links shadow property to source property
 */
export const UpstreamSchema = z.object({
    propertyId: z.string(), // Source property _id
    organizationId: z.string(), // Source org _id
    shareId: z.string(), // The share record (authorization token)
    shareSlug: z.string().nullable().optional(), // URL-safe share identifier for link construction
    isSyncActive: z.boolean(), // False if share revoked
    lastSyncedAt: z.coerce.date(), // For read-repair logic
    listingAgentName: z.string().optional(), // For UI display
    role: z.enum(['BUYER', 'CO_OWNER']).default('BUYER'), // v6.0: Shadow type discrimination
});
/**
 * Full Property schema (database document) - v3.2
 */
export const PropertySchema = z
    .object({
    _id: z.string(),
    organizationId: z.string(), // TENANT KEY - REQUIRED
    // Location
    address: AddressSchema,
    // Details
    price: z.number().positive().optional(),
    description: z.string().optional(),
    images: z.array(z.string()).default([]),
    bedrooms: z.number().int().min(0).optional(),
    bathrooms: z.number().min(0).optional(),
    squareFeet: z.number().positive().optional(),
    // Status
    status: PropertyStatusEnum.default('ACTIVE'),
    isActive: z.boolean().default(true),
    // v9.0: Archive cascade metadata (set when the property or a shadow is paused)
    archivedAt: z.union([z.string(), z.date()]).optional(),
    archivedBy: z.string().optional(),
    archivedReason: z
        .enum(['OWNER_ACTION', 'SOURCE_ARCHIVED', 'REMOVED_BY_OWNER', 'LEFT_BY_RECIPIENT'])
        .optional(),
    // v3.2: Shadow Property Fields
    isShadow: z.boolean().default(false),
    upstream: UpstreamSchema.nullable().optional(),
    // v3.1: Agent Representation (replaces isListing)
    representation: RepresentationEnum.optional(), // Optional during migration
    // v3.1: Internal Team Members (replaces assignedAgentIds)
    team: z.array(TeamMemberSchema).default([]),
    // Audit
    createdBy: z.string().optional(),
    // v3.0: Polymorphic Parties (external contacts)
    parties: z.array(PartySchema).default([]),
    // === DEPRECATED (v3.1) - kept for backward compat, remove in v3.2 ===
    /** @deprecated Use representation instead */
    isListing: z.boolean().optional(),
    /** @deprecated Use team instead */
    assignedAgentIds: z.array(z.string()).default([]),
    // DocuSign tracking
    envelopes: z.array(EnvelopeRecordSchema).default([]),
    // v3.0: Denormalized Stats
    stats: PropertyStatsSchema.optional(),
    // v3.3: Location Intelligence (fetched at property creation)
    locationIntelligence: LocationIntelligenceSchema.optional(),
    // v5.0: Aggregated Insights (from property-level synthesis)
    aggregatedInsights: AggregatedInsightsSchema.optional(),
    // v8.0: Market Data (from Perplexity deep research)
    marketData: MarketDataSchema.optional(),
})
    .merge(TimestampsSchema);
/**
 * Property creation input - v3.1
 * Uses representation to indicate seller/buyer agent
 */
export const CreatePropertyInputSchema = z.object({
    address: AddressSchema,
    price: z.number().positive().optional(),
    description: z.string().optional(),
    bedrooms: z.number().int().min(0).optional(),
    bathrooms: z.number().min(0).optional(),
    squareFeet: z.number().positive().optional(),
    // v3.1: representation - 'SELLER' or 'BUYER' (REQUIRED)
    representation: RepresentationEnum,
    // DEPRECATED: isListing - kept for backward compat
    isListing: z.boolean().optional(),
    // v5.2: Whether to add the creator as agent party (default: true on backend if omitted)
    addSelfAsAgent: z.boolean().optional(),
});
/**
 * Property update input
 */
export const UpdatePropertyInputSchema = z.object({
    address: AddressSchema.partial().optional(),
    price: z.number().positive().optional(),
    description: z.string().optional(),
    bedrooms: z.number().int().min(0).optional(),
    bathrooms: z.number().min(0).optional(),
    squareFeet: z.number().positive().optional(),
    status: PropertyStatusEnum.optional(),
    // representation is IMMUTABLE after creation — changing it corrupts chat memberships and shadow logic
    // DEPRECATED: isListing
    isListing: z.boolean().optional(),
});
/**
 * Property list query parameters
 */
export const PropertyListQuerySchema = z.object({
    status: PropertyStatusEnum.optional(),
    search: z.string().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    // v3.1: Filter by representation
    representation: RepresentationEnum.optional(),
    // DEPRECATED: isListing filter
    isListing: z.coerce.boolean().optional(),
});
/**
 * Property response (safe for client) - v3.0
 */
export const PropertyResponseSchema = PropertySchema;
/**
 * Property with populated contacts
 */
export const PropertyWithContactsSchema = PropertySchema.extend({
    sellers: z.array(z.object({
        _id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
    })).optional(),
    buyers: z.array(z.object({
        _id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
    })).optional(),
});
// =============================================================================
// Helper functions to work with parties
// =============================================================================
/**
 * Check if a property has sellers (parties with seller roles)
 */
export function hasSellers(property) {
    return property.parties.some(p => p.role === 'PRIMARY_SELLER' || p.role === 'CO_SELLER');
}
/**
 * Check if a property has buyers (parties with buyer roles)
 */
export function hasBuyers(property) {
    return property.parties.some(p => p.role === 'PRIMARY_BUYER' || p.role === 'CO_BUYER');
}
/**
 * Get seller parties from a property
 */
export function getSellers(property) {
    return property.parties.filter(p => p.role === 'PRIMARY_SELLER' || p.role === 'CO_SELLER');
}
/**
 * Get buyer parties from a property
 */
export function getBuyers(property) {
    return property.parties.filter(p => p.role === 'PRIMARY_BUYER' || p.role === 'CO_BUYER');
}
// =============================================================================
// v3.1: Representation/isListing Conversion Helpers
// =============================================================================
/**
 * Convert representation to isListing (for backward compat)
 */
export function representationToIsListing(representation) {
    return representation === 'SELLER';
}
/**
 * Convert isListing to representation
 */
export function isListingToRepresentation(isListing) {
    return isListing ? 'SELLER' : 'BUYER';
}
/**
 * Get effective representation from a property (handles migration)
 * Uses representation if set, otherwise derives from isListing
 */
export function getEffectiveRepresentation(property) {
    if (property.representation) {
        return property.representation;
    }
    // Fallback to isListing for properties not yet migrated
    return property.isListing === true ? 'SELLER' : 'BUYER';
}
/**
 * Check if property is a listing (seller representation)
 * Handles both new and legacy schemas
 */
export function isListingProperty(property) {
    return getEffectiveRepresentation(property) === 'SELLER';
}
// =============================================================================
// Image Upload Types
// =============================================================================
/**
 * Request for presigned image upload URL
 */
export const PresignedImageUploadRequestSchema = z.object({
    fileName: z.string().min(1),
    mimeType: z.string().default('image/jpeg'),
});
/**
 * Response from presigned image upload URL request
 */
export const PresignedImageUploadResponseSchema = z.object({
    uploadUrl: z.string().url(),
    s3Key: z.string(),
    expiresIn: z.number(),
});
/**
 * Input for adding an image to a property
 */
export const AddPropertyImageInputSchema = z.object({
    s3Key: z.string().min(1),
});
/**
 * Property image with download URL
 */
export const PropertyImageSchema = z.object({
    s3Key: z.string(),
    downloadUrl: z.string().url(),
});
// =============================================================================
// v3.2: Shadow Property Helper Functions
// =============================================================================
/**
 * Check if a property is a shadow property
 */
export function isShadowProperty(property) {
    return property.isShadow === true;
}
/**
 * Check if a shadow property's sync is active
 */
export function isShadowSyncActive(property) {
    return property.isShadow === true && property.upstream?.isSyncActive === true;
}
/**
 * Get the source property ID from a shadow property
 */
export function getSourcePropertyId(property) {
    if (!property.isShadow || !property.upstream) {
        return null;
    }
    return property.upstream.propertyId;
}
/**
 * Get the source organization ID from a shadow property
 */
export function getSourceOrganizationId(property) {
    if (!property.isShadow || !property.upstream) {
        return null;
    }
    return property.upstream.organizationId;
}
//# sourceMappingURL=property.js.map